import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  event_date: '',
  event_location: '',
  event_type: '',
  package: '', // chosen once packages are loaded
  addons: [],
  guest_count: '',
  additional_notes: '',
};

export const useBookingForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [pkgRes, addonRes] = await Promise.all([
          axios.get(`${API}/products/packages`),
          axios.get(`${API}/products/addons`),
        ]);
        const pkgs = pkgRes.data.packages || [];
        const addonList = addonRes.data.addons || [];
        setPackages(pkgs);
        setAddons(addonList);
        // Default to the popular package if any, else first
        const defaultPkg = pkgs.find(p => p.is_popular) || pkgs[0];
        if (defaultPkg) {
          setFormData((prev) => ({ ...prev, package: prev.package || defaultPkg.slug }));
        }
      } catch (err) {
        console.error('Failed to load product catalog:', err);
        setError('Could not load packages. Please refresh the page.');
      } finally {
        setCatalogLoading(false);
      }
    };
    loadCatalog();
    // One-time catalog fetch on mount only. `API` is a module-level constant
    // and the React setters are stable, so they aren't real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map: package slug → list of addon slugs included by default in that package
  const packageIncludes = useMemo(() => {
    const map = {};
    for (const a of addons) {
      for (const pkgSlug of a.included_in_packages || []) {
        if (!map[pkgSlug]) map[pkgSlug] = [];
        map[pkgSlug].push(a.slug);
      }
    }
    return map;
  }, [addons]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackageSelect = (packageSlug) => {
    setFormData((prev) => ({ ...prev, package: packageSlug }));
  };

  const handleAddonToggle = (addonSlug) => {
    setFormData((prev) => ({
      ...prev,
      addons: prev.addons.includes(addonSlug)
        ? prev.addons.filter((s) => s !== addonSlug)
        : [...prev.addons, addonSlug],
    }));
  };

  const isAddonIncluded = (addonSlug) =>
    packageIncludes[formData.package]?.includes(addonSlug) || false;

  const submit = (e) => {
    e.preventDefault();
    setError('');
    setConsentModalOpen(true);
  };

  const submitWithConsent = async (mediaConsent) => {
    setConsentModalOpen(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/bookings/inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.event_date,
        event_location: formData.event_location,
        event_type: formData.event_type,
        package: formData.package,
        addons: formData.addons,
        guest_count: formData.guest_count ? parseInt(formData.guest_count, 10) : null,
        additional_notes: formData.additional_notes,
        media_consent: Boolean(mediaConsent),
      });

      // Booking recorded on the backend — go straight to the "we'll call you" success page
      const quoteId = response.data?.quote?.id || '';
      window.location.href = `/booking-received?ref=${encodeURIComponent(quoteId)}`;
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
      setLoading(false);
    }
  };

  const closeConsentModal = () => setConsentModalOpen(false);

  return {
    formData,
    loading,
    error,
    catalogLoading,
    packages,
    addons,
    consentModalOpen,
    handleInputChange,
    handlePackageSelect,
    handleAddonToggle,
    isAddonIncluded,
    submit,
    submitWithConsent,
    closeConsentModal,
  };
};
